<template>
  <section v-if="deal && deal.state !== 'assessment'" class="deal-conditions buymodal-block__body" aria-label="Условия заявки">
    <div class="body-info">
    <h3 class="body-info__title">{{ readonly ? 'Согласованные условия' : 'Наше предложение' }}</h3>
    <p v-if="readonly" class="deal-conditions__state" role="status">{{ stateLabel }}</p>
    <p v-if="message" class="deal-conditions__message" role="alert">{{ message }}</p>
    <div v-if="needsReview" class="deal-conditions__message" role="alert">
      Заявка обновлена. Сохранённые условия показаны ниже. Проверьте выбор заново.
      <button type="button" class="btn cancel" @click="acceptServer">Проверить актуальные условия</button>
    </div>
    <div v-if="!readonly && types.length > 1" class="deal-conditions__offers" role="group" aria-label="Тип заявки">
      <button v-for="type in types" :key="type" type="button" class="deal-conditions__offer"
        :aria-pressed="String(draft.contractType === type)" :disabled="saving || needsReview" @click="selectType(type)">
        <span class="deal-conditions__offer-top"><span class="deal-conditions__offer-name">{{ typeLabel(type) }}</span><span class="deal-conditions__selected" aria-hidden="true">Выбрано</span></span>
        <span class="deal-conditions__offer-price"><span class="deal-conditions__price-prefix">до</span> {{ money(offers[type].total) }}</span>
        <span class="deal-conditions__offer-note">{{ type === 'tradein' ? `Включая скидку до ${money(offers.tradein.discount)}` : 'Денежная выплата за устройство' }}</span>
      </button>
    </div>
    <p v-if="!readonly && !types.length" role="alert">Нет доступного типа заявки. Обратитесь в поддержку.</p>
    <p v-if="!readonly && draft.contractType && !types.includes(draft.contractType)" class="deal-conditions__message" role="alert">Выбранный тип сейчас недоступен. Выберите разрешённый тип.</p>
    <div class="body-info__block screenThree deal-conditions__layout">
      <div class="info-table">
        <div class="info-table__line"><div class="label">Устройство</div><div class="value">{{ oldDeviceName }}</div></div>
        <div v-if="model.imei || $slots.identifier" class="info-table__line"><div class="label">{{ model.imei2 ? 'IMEI1' : user && user.flowType === 'mts' ? 'IMEI' : 'IMEI/SN' }}</div><div class="value"><slot name="identifier">{{ model.imei }}</slot></div></div>
        <div v-if="model.imei2" class="info-table__line"><div class="label">IMEI2</div><div class="value">{{ model.imei2 }}</div></div>
        <div class="info-table__line"><div class="label">Грейд</div><div class="value">{{ displayGrade }}</div></div>
        <div v-if="damages.length" class="info-table__line"><div class="label">Повреждения</div><div class="value"><div v-for="(damage, index) in damages" :key="index">{{ damage }}</div></div></div>
        <div v-if="readonly || types.length === 1" class="info-table__line">
          <div class="label">Тип заявки</div><div class="value">{{ typeLabel(readonly ? deal.contractType : types[0]) }}
            <button v-if="!readonly && draft.contractType !== types[0]" type="button" class="btn next" :disabled="saving || needsReview" @click="selectType(types[0])">Выбрать</button>
          </div>
        </div>
        <div v-if="selectedType === 'tradein'" class="info-table__line deal-conditions__device-row">
          <label class="label" :for="deviceSelectId">{{ readonly ? 'Согласованное новое устройство' : 'Покупаемое устройство' }}</label>
          <div class="value">
            <template v-if="readonly">{{ deviceName(deal.newDevice) }}</template>
            <template v-else>
              <multiselect :id="deviceSelectId" v-model="selectedDeviceOption" :options="deviceOptions"
                class="deal-conditions__select" label="label" track-by="value" :allow-empty="false"
                :show-labels="false" :max-height="260" :disabled="saving || needsReview"
                placeholder="Найти устройство" :option-height="58">
                <template slot="option" slot-scope="{ option }">
                  <span class="deal-conditions__device-name">{{ option.name }}</span>
                  <span v-if="option.discount !== null" class="deal-conditions__device-discount">Скидка до {{ money(option.discount) }}</span>
                </template>
                <template slot="noResult">Устройство не найдено</template>
              </multiselect>
              <p class="deal-conditions__hint">Можно оставить без выбора устройства.</p>
            </template>
          </div>
        </div>
        <div v-if="deal.actualNewDevice" class="info-table__line"><div class="label">Фактически купленное устройство</div><div class="value">{{ deviceName(deal.actualNewDevice) }}</div></div>
        <div v-if="deal.reportedCost !== null && deal.reportedCost !== undefined" class="info-table__line"><div class="label">Стоимость, переданная МТС</div><div class="value">{{ money(deal.reportedCost) }}</div></div>
        <div v-if="deal.refundDevice !== null && deal.refundDevice !== undefined" class="info-table__line"><div class="label">Старое устройство</div><div class="value">{{ deal.refundDevice ? 'Возвращено клиенту' : 'Осталось у нас' }}</div></div>
        <div v-if="readonly && selectedType === 'buyout' && deal.paymentStatus" class="info-table__line"><div class="label">Статус выплаты</div><div class="value">{{ paymentLabel }}</div></div>
      </div>
      <aside class="deal-conditions__summary" aria-label="Стоимость и скидка" aria-live="polite">
        <div class="deal-conditions__price">
          <div class="deal-conditions__price-title">{{ selectedType ? 'Итоговая стоимость:' : 'Выберите тип заявки' }}</div>
          <div class="deal-conditions__amount">{{ amounts.total === null ? '—' : money(amounts.total) }}</div>
          <div v-if="selectedType" class="deal-conditions__price-note">{{ selectedType === 'tradein' ? 'По Trade-in' : 'К выплате клиенту' }}</div>
        </div>
        <div v-if="selectedType === 'tradein'" class="deal-conditions__price deal-conditions__breakdown">
          <div><span class="deal-conditions__detail-label">Скидка до:</span><span class="deal-conditions__amount">{{ money(amounts.discount) }}</span></div>
          <div><span class="deal-conditions__detail-label">Цена устройства:</span><span class="deal-conditions__amount">{{ money(amounts.base) }}</span></div>
        </div>
      </aside>
    </div>
    <p v-if="readonly && selectedType === 'tradein'" class="deal-conditions__hint">Денежная выплата по Trade-in не предусмотрена.</p>
    </div>
  </section>
</template>

<script>
import { mapState, mapActions } from 'vuex';
import Multiselect from 'vue-multiselect';
import { isDealReadonly, dealDeviceName, allowedDealTypes, availableDealSubsidies, maxDealOffer, createDealDraft, previewDeal, dealSavePayload } from '@/utils/deal';

const stateLabels = {
  assessment: 'Оценка ещё не завершена', draft: 'Условия можно изменить до фиксации МТС',
  locked: 'Условия зафиксированы МТС. Ожидается получение старого устройства',
  buy: 'Получение старого устройства подтверждено', refund: 'Покупка нового устройства отменена. Старое устройство осталось у нас',
  cancel: 'Заявка отменена',
};
export default {
  components: { Multiselect },
  props: { viewOnly: Boolean, deviceLabel: String },
  data: () => ({ draft: null, deviceSelection: null, dirty: false, needsReview: false, saving: false, refreshing: false, message: '', refreshTimer: null }),
  computed: {
    ...mapState('buyphones', ['deal', 'model', 'subsidies']),
    ...mapState('user', ['user']),
    readonly() { return this.viewOnly || isDealReadonly(this.deal); },
    types() { return allowedDealTypes(this.deal); },
    availableSubsidies() { return availableDealSubsidies(this.subsidies); },
    offers() { return { buyout: maxDealOffer(this.deal, 'buyout', this.availableSubsidies), tradein: maxDealOffer(this.deal, 'tradein', this.availableSubsidies) }; },
    preview() { return previewDeal(this.deal, this.draft, this.availableSubsidies); },
    selectedType() { return this.readonly ? this.deal.contractType : this.draft.contractType; },
    amounts() { return this.readonly ? { base: this.deal.devicePrice, discount: this.deal.sumDiscount, total: this.deal.totalSum } : this.preview; },
    deviceSelectId() { return `deal-new-device-${this._uid}`; },
    deviceOptions() {
      const options = [{ value: null, name: 'Без нового устройства', discount: null, label: 'Без нового устройства' }];
      if (this.deal.contractType === 'tradein' && this.deal.newDevice) {
        const name = this.deviceName(this.deal.newDevice);
        options.unshift({ value: 'preserve', name, discount: this.deal.sumDiscount, label: `${name} — скидка до ${this.money(this.deal.sumDiscount)}` });
      }
      return options.concat(this.availableSubsidies.map(item => ({
        value: item.subsidyId, name: item.deviceModel, discount: Number(item.subsidyPrice),
        label: `${item.deviceModel} — скидка до ${this.money(Number(item.subsidyPrice))}`,
      })));
    },
    selectedDeviceOption: {
      get() { return this.deviceOptions.find(option => option.value === this.deviceSelection) || null; },
      set(option) {
        if (!option || this.readonly || this.saving || this.needsReview) return;
        this.deviceSelection = option.value;
        this.selectDevice();
      },
    },
    damages() {
      const value = this.model && this.model.mobile && this.model.mobile.damageList;
      return typeof value === 'string' ? value.split(',').map(item => item.trim()).filter(Boolean) : [];
    },
    oldDeviceName() {
      if (this.deviceLabel) return this.deviceLabel;
      const mobile = this.model && this.model.mobile || {};
      const name = mobile.modelOrigin || mobile.name || '';
      const capacity = mobile.Capacity || '';
      const includesCapacity = name.replace(/\s/g, '').toLowerCase().includes(String(capacity).replace(/\s/g, '').toLowerCase());
      return [mobile.manufacture, name, !includesCapacity && capacity].filter(Boolean).join(' ') || 'Устройство';
    },
    displayGrade() {
      const grade = this.deal.grade;
      return this.user && this.user.flowType === 'mts' ? ({ 'Grade A': 'Grade H', 'Grade С': 'Grade C', 'Grade E': 'Grade Z' })[grade] || grade : grade;
    },
    stateLabel() { return stateLabels[this.deal.state] || this.deal.state; },
    paymentLabel() {
      return ({ done: 'Выплачено', fail: 'Ошибка выплаты', progress: 'Обрабатывается', paid: 'Выплачено', pending: 'Ожидается', processing: 'Обрабатывается', failed: 'Ошибка выплаты', notapplicable: 'Не предусмотрена' })[this.deal.paymentStatus] || this.deal.paymentStatus;
    },
  },
  watch: {
    deal: {
      immediate: true,
      handler(current, previous) {
        if (!current) return;
        if (!this.draft || isDealReadonly(current)) { this.acceptServer(); return; }
        if (this.saving) return;
        const changed = previous && (current.revision !== previous.revision || current.organizationMode !== previous.organizationMode || JSON.stringify(current.allowedTypes) !== JSON.stringify(previous.allowedTypes));
        if (changed && this.dirty) this.needsReview = true;
        else if (!this.dirty) this.acceptServer();
      },
    },
  },
  mounted() {
    window.addEventListener('focus', this.refresh);
    document.addEventListener('visibilitychange', this.onVisibility);
    this.refresh();
    this.refreshTimer = window.setInterval(this.refresh, 10000);
  },
  beforeDestroy() {
    window.removeEventListener('focus', this.refresh);
    document.removeEventListener('visibilitychange', this.onVisibility);
    window.clearInterval(this.refreshTimer);
  },
  methods: {
    ...mapActions('buyphones', ['refreshDeal', 'saveDeal']),
    deviceName: dealDeviceName,
    typeLabel(type) { return ({ buyout: 'Выкуп', tradein: 'Trade-in' })[type] || 'Не выбран'; },
    money(value) { return typeof value === 'number' && Number.isFinite(value) ? `${new Intl.NumberFormat('ru-RU').format(value)} ₽` : 'Не определена'; },
    markDirty() { this.dirty = true; this.message = ''; this.$emit('draft-change'); },
    selectType(type) {
      if (this.readonly || this.saving || this.needsReview || !this.types.includes(type)) return;
      this.draft.contractType = type;
      this.markDirty();
    },
    selectDevice() { this.draft.subsidy = this.deviceSelection === 'preserve' ? undefined : this.deviceSelection; this.markDirty(); },
    acceptServer() {
      this.draft = createDealDraft(this.deal, this.model && this.model.initialContractType);
      this.deviceSelection = this.deal.contractType === 'tradein' && this.deal.newDevice ? 'preserve' : null;
      this.dirty = false;
      this.needsReview = false;
    },
    onVisibility() { if (!document.hidden) this.refresh(); },
    async refresh() {
      if (this.refreshing || this.saving || document.hidden || !this.model) return;
      this.refreshing = true;
      try { await this.refreshDeal({ btag: this.model.btag }); }
      catch (error) { this.message = 'Не удалось обновить заявку. Проверьте соединение.'; }
      finally { this.refreshing = false; }
    },
    async save() {
      if (this.saving) return false;
      if (isDealReadonly(this.deal)) return this.deal.state !== 'cancel' && this.deal.state !== 'refund' && this.deal.state !== 'assessment';
      if (this.needsReview || !this.types.includes(this.draft.contractType)) {
        this.message = 'Проверьте доступный тип и актуальные условия заявки.';
        return false;
      }
      this.saving = true;
      this.message = '';
      try {
        await this.saveDeal(dealSavePayload(this.model.btag, this.deal, this.draft));
        this.acceptServer();
        return true;
      } catch (error) {
        const data = error.response && error.response.data || {};
        const messages = {
          dealVersionConflict: 'Заявка обновлена. Проверьте выбранные условия заново.',
          dealTypeUnavailable: 'Тип заявки больше недоступен. Проверьте разрешённые варианты.',
          subsidyUnavailable: 'Скидка недоступна. Выберите другое устройство или «Без нового устройства».',
          assessmentRequired: 'Для продолжения завершите оценку устройства.',
          buyoutSideUnknown: 'Невозможно определить сторону выкупа. Обратитесь в службу поддержки.',
          dealLocked: 'Условия уже зафиксированы МТС.', dealTerminal: 'Заявка уже завершена, отменена или возвращена.',
          orderNotFound: 'Заявка не найдена.', invalidDealSelection: 'Проверьте тип заявки и выбранное устройство.',
        };
        this.message = messages[data.error] || data.ErrorDetails || 'Не удалось подтвердить сохранение. Состояние заявки перечитывается; проверьте условия перед повтором.';
        // A lost response may have followed a successful save. Read; never replay automatically.
        try { await this.refreshDeal({ btag: this.model.btag }); }
        catch (refreshError) { this.message += ' Не удалось обновить заявку.'; }
        if (!isDealReadonly(this.deal)) this.needsReview = true;
        return false;
      } finally { this.saving = false; }
    },
  },
};
</script>

<style scoped>
.deal-conditions .body-info__title { margin: 0 0 14px; }
.deal-conditions__state { margin: 0 0 16px; }
.deal-conditions__message { padding: 12px; margin: 0 0 16px; background: #fff3dc; }
.deal-conditions__offers { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 16px; padding: 5px 0 24px; }
.deal-conditions__offer { padding: 17px 19px; border: 1px solid #d4d4d4; border-radius: 5px; background: #fff; color: #333; text-align: left; font: inherit; display: flex; flex-direction: column; gap: 9px; min-width: 0; }
.deal-conditions__offer[aria-pressed="true"] { border-color: #005afc; box-shadow: inset 0 0 0 1px #005afc; background: #f5f8ff; }
.deal-conditions__offer:disabled { cursor: default; }
.deal-conditions__offer-top { display: flex; justify-content: space-between; align-items: center; gap: 12px; }
.deal-conditions__offer-name { font-size: 16px; font-weight: 500; }
.deal-conditions__offer-price { font-size: 24px; line-height: 30px; font-weight: 500; color: #000; font-variant-numeric: tabular-nums; }
.deal-conditions__price-prefix { font-size: 16px; font-weight: 400; }
.deal-conditions__selected { visibility: hidden; font-size: 12px; font-weight: 500; color: #005afc; }
.deal-conditions__offer[aria-pressed="true"] .deal-conditions__selected { visibility: visible; }
.deal-conditions__offer[aria-pressed="true"] .deal-conditions__offer-name { color: #005afc; }
.deal-conditions__offer-note { font-size: 12px; color: #666; }
.deal-conditions__layout { align-items: flex-start; }
.deal-conditions .info-table { min-width: 0; }
.deal-conditions .info-table__line { display: grid !important; grid-template-columns: 190px minmax(0, 1fr); gap: 15px; line-height: 20px; }
.deal-conditions .info-table__line > .label { min-width: 0 !important; }
.deal-conditions .info-table__line > .value { min-width: 0; overflow-wrap: anywhere; }
.deal-conditions__device-row { align-items: flex-start !important; border-bottom: 0 !important; }
.deal-conditions__device-row > .label { padding-top: 9px; }
.deal-conditions__select { position: relative; display: block; width: 100%; max-width: 100%; min-width: 0; min-height: 44px !important; max-height: none; margin: 0; font: inherit; }
.deal-conditions__select >>> .multiselect__tags { min-height: 44px; max-height: none; max-width: none; margin: 0; padding: 11px 34px 11px 12px; border: 1px solid #d4d4d4; border-radius: 5px; background: #fff; }
.deal-conditions__select:focus-within >>> .multiselect__tags { border-color: #005afc; box-shadow: 0 0 0 1px #005afc; }
.deal-conditions__select >>> .multiselect__single,
.deal-conditions__select >>> .multiselect__input { margin: 0; padding: 0 !important; border: 0; font: inherit; font-size: 14px; line-height: 20px; color: #333; white-space: normal; overflow-wrap: anywhere; }
.deal-conditions__select >>> .multiselect__input::placeholder { color: #777; }
.deal-conditions__select >>> .multiselect__select { position: absolute; top: 0; right: 0; width: 32px; height: 44px; transform: none; }
.deal-conditions__select >>> .multiselect__select:after { position: absolute; top: 19px; right: 12px; width: 0; height: 0; border: 5px solid transparent; border-bottom: 0; border-top-color: #005afc; background: none; }
.deal-conditions__select >>> .multiselect__content-wrapper { position: absolute; top: 100%; bottom: auto; left: 0; width: 100%; min-width: 0; max-width: 100%; overflow-y: auto; overflow-x: hidden; border: 1px solid #d4d4d4; border-radius: 5px; box-shadow: 0 6px 18px rgba(0, 0, 0, .12); }
.deal-conditions__select.multiselect--above >>> .multiselect__content-wrapper { top: auto; bottom: 100%; margin-bottom: 5px; }
.deal-conditions__select >>> .multiselect__content { position: static; width: 100%; max-width: 100%; max-height: none; margin: 0; padding: 0; border: 0; overflow: visible; }
.deal-conditions__select >>> .multiselect__option { min-height: 44px; padding: 10px 12px 10px 32px; line-height: 20px; white-space: normal; overflow-wrap: anywhere; }
.deal-conditions__select >>> .multiselect__option--highlight { background: #e2f1ff; color: #333; }
.deal-conditions__select >>> .multiselect__option--selected { background: #f5f8ff !important; }
.deal-conditions__select >>> .multiselect__option--selected:before { left: 10px; }
.deal-conditions__device-name { display: block; font-size: 14px; }
.deal-conditions__device-discount { display: block; font-size: 12px; color: #005afc; }
.deal-conditions__hint { margin: 7px 0 0; font-size: 12px; line-height: 18px; color: #777; }
.deal-conditions__summary { width: 190px; flex: 0 0 190px; display: flex; flex-direction: column; gap: 20px; }
.deal-conditions__price { padding: 8px 15px; border-radius: 2px; background: rgba(0, 0, 0, .06); text-align: center; color: #000; font-size: 17px; }
.deal-conditions__price-title { font-weight: 500; }
.deal-conditions__amount { font-variant-numeric: tabular-nums; white-space: nowrap; }
.deal-conditions__price-note { margin-top: 2px; font-size: 12px; color: #666; }
.deal-conditions__breakdown { display: flex; flex-direction: column; gap: 10px; font-size: 14px; }
.deal-conditions__detail-label { display: block; margin-bottom: 2px; font-size: 16px; }
@media (max-width: 900px) {
  .deal-conditions__summary { flex: none; width: 100%; flex-direction: row; gap: 15px; }
  .deal-conditions__price { flex: 1; }
}
@media (max-width: 620px) {
  .deal-conditions .body-info { padding: 24px 20px 0; }
  .deal-conditions .info-table__line { grid-template-columns: 1fr; gap: 8px; }
  .deal-conditions__device-row > .label { padding-top: 0; }
  .deal-conditions__select { font-size: 16px; }
  .deal-conditions__summary { flex-direction: column; gap: 10px; }
  .deal-conditions__breakdown { flex-direction: row; justify-content: space-around; }
}
@media (max-width: 540px) {
  .deal-conditions__offers { grid-template-columns: 1fr; gap: 12px; }
}
</style>
